import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ThemeColors } from '../../../../Context/ThemeContext';
import type { CustomDateRange } from '../types';

interface DateRangePickerProps {
  value?: CustomDateRange;
  onChange: (range: CustomDateRange | undefined) => void;
  theme: ThemeColors;
  style?: React.CSSProperties;
  isActive?: boolean;
  popoverAlign?: 'left' | 'right';
}

const toComparisonDays = (override: CustomDateRange['comparisonOverride']): number | undefined => {
  if (!override) return undefined;
  switch (override.unit) {
    case 'days': return override.value;
    case 'weeks': return override.value * 7;
    case 'months': return override.value * 30;
  }
};

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getComparisonRange = (startDate: string, endDate: string, override?: CustomDateRange['comparisonOverride']): { start: string; end: string } => {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const rangeDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  const compDays = override ? (toComparisonDays(override) ?? rangeDays) : rangeDays;

  const compEnd = new Date(start);
  compEnd.setDate(compEnd.getDate() - 1);
  const compStart = new Date(compEnd);
  compStart.setDate(compStart.getDate() - compDays + 1);

  return {
    start: compStart.toISOString().split('T')[0],
    end: compEnd.toISOString().split('T')[0],
  };
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  theme,
  style,
  isActive,
  popoverAlign = 'right',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [startDate, setStartDate] = useState(value?.startDate || '');
  const [endDate, setEndDate] = useState(value?.endDate || '');
  const [touched, setTouched] = useState(false);
  const [useOverride, setUseOverride] = useState(!!value?.comparisonOverride);
  const [overrideValue, setOverrideValue] = useState(value?.comparisonOverride?.value || 7);
  const [overrideUnit, setOverrideUnit] = useState<'days' | 'weeks' | 'months'>(value?.comparisonOverride?.unit || 'days');

  useEffect(() => {
    if (value) {
      setStartDate(value.startDate);
      setEndDate(value.endDate);
      setUseOverride(!!value.comparisonOverride);
      if (value.comparisonOverride) {
        setOverrideValue(value.comparisonOverride.value);
        setOverrideUnit(value.comparisonOverride.unit);
      }
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const comparisonRange = useMemo(() => {
    if (!startDate || !endDate) return null;
    return getComparisonRange(startDate, endDate, useOverride ? { value: overrideValue, unit: overrideUnit } : undefined);
  }, [startDate, endDate, useOverride, overrideValue, overrideUnit]);

  const isValid = startDate && endDate && startDate <= endDate;

  const validationMessage = useMemo((): string | null => {
    if (!touched) return null;
    if (!startDate && !endDate) return null;
    if (!startDate) return 'Start date is required';
    if (!endDate) return 'End date is required';
    if (startDate > today) return 'Start date cannot be in the future';
    if (endDate > today) return 'End date cannot be in the future';
    if (startDate > endDate) return 'Start date cannot be after end date';
    return null;
  }, [touched, startDate, endDate, today]);

  const handleApply = () => {
    if (!isValid) return;
    onChange({
      startDate,
      endDate,
      comparisonOverride: useOverride ? { value: overrideValue, unit: overrideUnit } : undefined,
    });
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(undefined);
    setStartDate('');
    setEndDate('');
    setTouched(false);
    setUseOverride(false);
    setOverrideValue(7);
    setOverrideUnit('days');
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'absolute', zIndex: 11, ...style }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        title="Custom date range"
        style={{
          background: isActive ? '#2563eb' : 'transparent',
          color: isActive ? '#fff' : theme.textSecondary,
          padding: '2px 5px',
          borderRadius: '4px',
          fontSize: '12px',
          border: isActive ? 'none' : `1px solid ${theme.border}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          lineHeight: 1,
        }}
      >
        &#128197;
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '28px',
            ...(popoverAlign === 'left' ? { left: '0' } : { right: '0' }),
            background: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            padding: '12px',
            minWidth: '260px',
            zIndex: 100,
          }}
        >
          <div style={{ fontSize: '11px', color: theme.textSecondary, fontWeight: 500, marginBottom: '8px' }}>
            Custom Date Range
          </div>

          {/* Start Date */}
          <label style={{ fontSize: '11px', color: theme.textSecondary, display: 'block', marginBottom: '2px' }}>Start</label>
          <input
            type="date"
            value={startDate}
            max={endDate || today}
            onChange={(e) => { setStartDate(e.target.value); setTouched(true); }}
            style={{
              width: '100%',
              padding: '6px 8px',
              borderRadius: '4px',
              border: `1px solid ${theme.border}`,
              background: theme.inputBg,
              color: theme.text,
              fontSize: '12px',
              marginBottom: '8px',
              boxSizing: 'border-box',
            }}
          />

          {/* End Date */}
          <label style={{ fontSize: '11px', color: theme.textSecondary, display: 'block', marginBottom: '2px' }}>End</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={today}
            onChange={(e) => { setEndDate(e.target.value); setTouched(true); }}
            style={{
              width: '100%',
              padding: '6px 8px',
              borderRadius: '4px',
              border: `1px solid ${theme.border}`,
              background: theme.inputBg,
              color: theme.text,
              fontSize: '12px',
              marginBottom: '10px',
              boxSizing: 'border-box',
            }}
          />

          {/* Validation Message */}
          {validationMessage && (
            <div style={{ fontSize: '11px', color: '#ef4444', marginBottom: '6px', marginTop: '-4px' }}>
              {validationMessage}
            </div>
          )}

          {/* Comparison Period Info */}
          {comparisonRange && (
            <div style={{ fontSize: '11px', color: theme.textSecondary, marginBottom: '8px', padding: '6px 8px', background: theme.inputBg, borderRadius: '4px' }}>
              Comparing to: {formatDate(comparisonRange.start)} &ndash; {formatDate(comparisonRange.end)}
            </div>
          )}

          {/* Comparison Override */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: theme.textSecondary, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={useOverride}
                onChange={(e) => setUseOverride(e.target.checked)}
                style={{ margin: 0 }}
              />
              Custom comparison window
            </label>

            {useOverride && (
              <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={overrideValue}
                  onChange={(e) => setOverrideValue(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  style={{
                    width: '60px',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: `1px solid ${theme.border}`,
                    background: theme.inputBg,
                    color: theme.text,
                    fontSize: '12px',
                  }}
                />
                <select
                  value={overrideUnit}
                  onChange={(e) => setOverrideUnit(e.target.value as 'days' | 'weeks' | 'months')}
                  style={{
                    flex: 1,
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: `1px solid ${theme.border}`,
                    background: theme.inputBg,
                    color: theme.text,
                    fontSize: '12px',
                  }}
                >
                  <option value="days">days</option>
                  <option value="weeks">weeks</option>
                  <option value="months">months</option>
                </select>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={handleApply}
              disabled={!isValid}
              style={{
                flex: 1,
                padding: '6px 12px',
                borderRadius: '4px',
                border: 'none',
                background: isValid ? '#2563eb' : '#4b5563',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 600,
                cursor: isValid ? 'pointer' : 'default',
                opacity: isValid ? 1 : 0.5,
              }}
            >
              Apply
            </button>
            {isActive && (
              <button
                onClick={handleClear}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: `1px solid ${theme.border}`,
                  background: 'transparent',
                  color: theme.text,
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper to convert CustomDateRange to comparison days for API
export const getComparisonDays = (range: CustomDateRange): number | undefined => {
  return toComparisonDays(range.comparisonOverride);
};
